import React, { useState, useEffect, useRef } from "react";
import useFilters from "../../../../hooks/useFilters";
import FileSaver from "file-saver";

import { useHistory } from "react-router-dom";
import { useStore } from "../../../../store/context";
import i18n from "i18n";

import { OrderService } from "../../../../services/OrderService";
import { PublicService } from "../../../../services/PublicService";
import { formatDateToYyyyMmDd } from "helpers/Parsers";

import AdminLayout from "../../../../components/MainApp/layouts/DesktopLayout";
import MainNotification from "../../../../components/MainApp/atoms/Notification";
import FilterPanel from "../../../../components/MainApp/organisms/Filters/FilterPanel";
import OrdersTable from "../../../../components/MainApp/organisms/Orders/PrepOrders/Table";
import Pagination from "../../../../components/MainApp/atoms/Pagination";
import Loader from "../../../../components/MainApp/atoms/Loader";

import tableHeaders from "./data/tableHeaders";
import {
  filterData as FilterDataSource,
  filterTypeMap
} from "./data/filterData";
import prepTypeMapping from "./data/PrepTypeMapping";
import { isMobile } from "../../../../helpers/Mobile";
import styles from "./PrepOrders.module.scss";
import excel from "assets/images/excel.svg";
import excelwhite from "assets/images/excelwhite.svg";
import SimpleSearchPanel from "components/MainApp/organisms/Filters/SimpleSearchPanel";
import { NotFoundItems } from "views/Admin/components/NotFoundItems";
import { capitalize } from "helpers/utils/utils_general";

const initialFilters = {
  page_size: 10,
  search: "",
  ordering: "-created_at",
  status: undefined,
  node: undefined,
  destination: undefined, 
  date: [null, null],
};

let scrolling = false;

export const PrepOrdersView = () => {
  const [prepOrderFilters, setPrepOrderFilters] = useState();
  const history = useHistory();
  const [loading, setloading] = useState(false);
  const [noItems, setNoItems] = useState(false);
  const [filterData, setFilterData] = useState(FilterDataSource);

  const [orders_name, setOrders_name] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const myRef = useRef(0);

  const [
    filters,
    setFilters,
    selectedFiltersAll,
    selectedFiltersByType,
    handleApplyFilters,
    handleSearch,
    headerOrderingHandler,
    resetOrdering,
    handleFiltersChangeOrdering,
    applied,
    page,
    setPage,
    items,
    setItems,
    pager,
    setPager,
    header,
    setHeader,
    mobilePanelOpen,
    setMobilePanelOpen
  ] = useFilters(
    initialFilters,
    prepOrderFilters,
    filterTypeMap,
    isMobile,
    tableHeaders
  );

  const headerOptions = [
    {
      icon: "icon-iconos_mas2",
      name: "add",
      handler: () => {
        history.push("/admin/orders/preparation/load");
      },
      tooltip: i18n.t("create.order")
    }
  ];

  useEffect(() => {
    /**
     * Initial data fetch:
     * - Populates filter options from PublicService.
     * - Retrieves order codes for search suggestions.
     */
    fillFilterData();
    OrderService.ordersCode({ "type": "PRE" }).then(response => {
      setOrders_name(response);
    });
  }, []);

  useEffect(() => {
    setMobilePanelOpen(false);
    const first_load =
      JSON.stringify(initialFilters) === JSON.stringify(filters);
    filter(first_load);

    /**
     * Table Header Synchronization:
     * Aligns UI headers with the current ordering state from the useFilters hook.
     */
    const keyOrdering =
      filters.ordering.charAt(0) === "-"
        ? filters.ordering.substr(1)
        : filters.ordering;

    if (keyOrdering in tableHeaders) {
      const newHeaders = { ...header };
      newHeaders[keyOrdering]["active"] = true;
      newHeaders[keyOrdering]["direction"] =
        filters.ordering === keyOrdering ? true : false;
      setHeader(newHeaders);
    }
  }, []);

  useEffect(() => {
    /**
     * Side-effect to handle filter or pagination changes.
     * 1. Skip initial mount execution.
     * 2. Trigger data fetch based on new filter/page state.
     * 3. Sync local state with hook state for persistence.
     */
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }

    filter(false);

    setPrepOrderFilters({
      filters: filters,
      selectedFiltersAll: selectedFiltersAll,
      selectedFiltersByType: selectedFiltersByType
    });

  }, [filters, page]); // Only react to filter or page index changes

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  });

  const handleScroll = event => {
    if (!isMobile) return;

    if (
      event.srcElement.offsetHeight + event.target.scrollTop + 100 <
      event.srcElement.scrollHeight
    )
      return;

    if (!pager.next || submitted || scrolling) return;
    scrolling = true;

    setPage(page + 1);
  };

  const handlePagerChange = function (page) {
    setPage(page);
  };

  /**
   * Main data fetching function for Preparation Orders.
   * Handles parameter normalization and conditional item appending for mobile.
   */
  const filter = function (firstLoad = false) {
    // Append results only for mobile infinite scroll beyond the first page
    let append = isMobile && page > 1;
    if (!append) {
      setItems([]);
    }

    //BEFORE SUBMIT
    document.body.style.cursor = "wait";
    setSubmitted(true);
    // setErrors({})
    // setMessage({})

    // Normalize array-based filters into comma-separated strings for API requirements
    const data = { ...filters, page: page };
    data["status"] = data["status"] ? data["status"].join(",") : undefined;
    data["node"] = data["node"] ? data["node"].join(",") : undefined;
    data["destination"] = data["destination"]
      ? data["destination"].join(",")
      : undefined;

    /**
     * SAFE DATE FORMATTING & CLEANUP:
     * Parses date ranges into YYYY-MM-DD. 
     * Explicitly resets parameters to undefined if no date is selected 
     * to ensure stale filters are cleared from the request.
     */
    if (data["date"] && Array.isArray(data["date"]) && data["date"][0]) {
      data["from_date"] = formatDateToYyyyMmDd(data["date"][0]);
      data["from_date_to"] = data["date"][1] ? formatDateToYyyyMmDd(data["date"][1]) : undefined;
    } else {
      data["from_date"] = undefined;
      data["from_date_to"] = undefined;
    }
    delete data["date"];

    setloading(true);
    OrderService.prepOrders(data)
      .then(response => {
        //this.afterSubmit();
        setSubmitted(false);
        document.body.style.cursor = "default";
        scrolling = false;

        // if (firstLoad && response.count === 0) {
        //   history.push("/admin/products/load");
        //   setNoProducts(true)
        //   return;
        // }

        const newOrders = append
          ? [...items, ...response.results]
          : response.results;

        setloading(false);
        setItems(newOrders);
        setPager({
          next: response.next,
          previous: response.previous,
          count: response.count,
          countPages: Math.ceil(response.count / filters.page_size)
        });
      })
      .catch(e => {
        setSubmitted(false);
        setloading(false);
        document.body.style.cursor = "default";
        scrolling = false;
      });
  };

  /**
   * Dynamically populates filter options based on configuration and platform.
   */
  const fillFilterData = function () {
    /**
     * Populate Node options (Origin).
     * Note: Options are loaded but visibility follows the 'enabled: false' 
     * flag defined in the data source.
     */
    PublicService.nodes().then(rta => {
      const options = rta.map(n => ({
        ...n,
        label: n.name,
        selected: false,
        type: "node",
        id: n.code
      }));

      setFilterData(prevData => {
        const newData = [...prevData];
        if (newData[1]) {
          newData[1].options = options;
          // Note: Visibility is explicitly controlled by data source config.
        }
        return newData;
      });
    });

    /**
     * Mobile Sorting Injection:
     * Injects the 'ordering' filter dynamically for mobile users.
     */
    if (isMobile) {
      setFilterData(prevData => {
        // Prevent duplicate injection during re-renders by checking for the unique 'ordering' identifier
        const hasOrdering = prevData.some(f => f.name === "ordering");
        if (!hasOrdering) {
          return [
            ...prevData,
            {
              name: "ordering",
              type: "twoOption",
              options: [
                {
                  selected: false,
                  label: i18n.t("less.recent"),
                  value: "created_at"
                }
              ],
              label: i18n.t("order.by"),
              firstOptionLabel: i18n.t("latest"),
              value: "-created_at",
              enabled: true // Required for the Mobile sorting selector
            }
          ];
        }
        return prevData;
      });
    }
  };

  const downloadReport = () => {
    const data = { ...filters };
    data["status"] = data["status"] ? data["status"].join(",") : undefined;
    data["node"] = data["node"] ? data["node"].join(",") : undefined;
    data["destination"] = data["destination"]
      ? data["destination"].join(",")
      : undefined;

    setloading(true);
    OrderService.prepDownloadReport(data)
      .then(blob => {
        setloading(false);
        FileSaver.saveAs(blob, "reporte.xls");
      })
      .catch(error => {
        setloading(false);
      });
  };

  const goToDetailPage = event => {
    history.push(
      `/admin/orders/preparation/details/${event.currentTarget.dataset.requestorderId}`
    );
  };

  const goToEditPage = event => {
    history.push(
      `/admin/orders/preparation/edit/${event.currentTarget.dataset.requestorderId}`
    );
  };

  const headerButtons = (
    <div
      className="excel-button"
      onClick={downloadReport}
      style={{
        width: "30px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
      }}
    >
      <img style={{ height: "30px" }} src={isMobile ? excelwhite : excel} />{" "}
    </div>
  );

  const mobileContent = (
    <React.Fragment>
      <div className={styles["orders-request-table-filter-container"]}>
        <SimpleSearchPanel
          placeholder={i18n.t("search.by.name.or.code")}
          handleSubmit={handleSearch}
        />
      </div>

      <div
        className={`${styles["orders-request-table-mobile-panel"]} ${mobilePanelOpen ? styles["opened"] : ""}`}
      >
        <FilterPanel
          resultList={orders_name}
          placeholder={i18n.t("search.by.name.or.code")}
          searchValue={filters.search}
          handleSubmitSearch={handleSearch}
          filters={filterData}
          selectedFiltersAll={selectedFiltersAll}
          selectedFiltersByType={selectedFiltersByType}
          handleApplyFilters={handleApplyFilters}
          closeMobilePanel={() => {
            setMobilePanelOpen(false);
          }}
        ></FilterPanel>
      </div>
      <div className={styles["orders-request-table-table"]}>
        <div className={styles["orders-request-table-top-table"]}>
          <div className={styles["orders-request-table-top-total"]}>
            {capitalize(i18n.t("total"))}{" "}
            <span className={styles["orders-request-table-top-total-bold"]}>
              ({pager.count})
            </span>
          </div>
          <div
            className={`${styles["orders-request-table-top-sort"]} ${applied ? styles["orders-request-table-active-filter"] : ""}`}
            onClick={() => {
              setMobilePanelOpen(true);
            }}
          >
            <i className={`icon-iconos_filtros ${styles["orders-request-table-top-icon"]}`}></i>
          </div>
        </div>
        <OrdersTable
          requestOrders={items}
          headers={header}
          handleClickRequestOrder={goToDetailPage}
          handleClickDraft={goToEditPage}
          mapTypes={prepTypeMapping}
        />
      </div>

      {items.length === 0 && !submitted && (
        <NotFoundItems />
      )}
    </React.Fragment>
  );

  // DESKTOP START
  const desktopContent = (
    <div className={styles["orders-request-content-wrapper"]}>
      <FilterPanel
        resultList={orders_name}
        placeholder={i18n.t("search.by.name.or.code")}
        searchValue={filters.search}
        handleSubmitSearch={handleSearch}
        filters={filterData}
        selectedFiltersAll={selectedFiltersAll}
        selectedFiltersByType={selectedFiltersByType}
        handleApplyFilters={handleApplyFilters}
      ></FilterPanel>

      {items.length > 0 && (
        <div className={styles["orders-request-table-table"]}>
          <div className={styles["orders-request-table-top-table"]}>
            <div className={styles["orders-request-table-top-total"]}>
              {i18n.t("total").toUpperCase()}{" "}
              <span className={styles["orders-request-table-top-total-bold"]}>
                ({pager.count})
              </span>
            </div>
            <div className={styles["orders-request-table-top-sort"]}>
              <div
                data-automation-id="orders-request-table-sort"
                className={
                  filters.ordering.includes("created_at")
                    ? `${styles["orders-request-table-top-sort"]} ${styles["orders-request-table-active-filter"]}`
                    : styles["orders-request-table-top-sort"]
                }
                onClick={() => {
                  handleFiltersChangeOrdering(
                    "ordering",
                    filters.ordering === "-created_at"
                      ? "created_at"
                      : "-created_at"
                  );
                  resetOrdering();
                }}
              >
                {filters.ordering === "created_at" ? (
                  <React.Fragment>
                    {i18n.t("less.recent")}{" "}
                    <i className={`icon-chevron ${styles["orders-request-table-top-icon"]}`}></i>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {" "}
                    {i18n.t("latest")}{" "}
                    <i className={`icon-chevron-down ${styles["orders-request-table-chevron-down"]} ${styles["orders-request-table-top-icon"]}`}></i>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <OrdersTable
            requestOrders={items}
            headers={tableHeaders}
            headerClickHandler={headerOrderingHandler}
            handleClickRequestOrder={goToDetailPage}
            handleClickDraft={goToEditPage}
            mapTypes={prepTypeMapping}
          />
        </div>
      )}

      {items.length === 0 && !submitted && (
        <NotFoundItems />
      )}
      {items.length > 0 && (
        <div>
          <Pagination
            pageQuantity={pager.countPages}
            currentPage={page}
            pageChangeHandler={handlePagerChange}
          />
        </div>
      )}
    </div>
  );
  // DESKTOP END

  const content = (
    <div className={styles["orders-request-content-container"]}>
      {isMobile ? mobileContent : desktopContent}
    </div>
  );

  return (
    <div className={styles["orders-request-container"]}>
      <AdminLayout
        headerTitle={i18n.t("orders.request.out")}
        headerOptions={headerOptions}
        content={content}
        navHidden={false}
        customButtons={headerButtons}
        goBackFunc={() => {
          history.push("/admin/orders");
        }}
      ></AdminLayout>
      {loading && <Loader />}
      <MainNotification
        label={i18n.t("product.updated.successfully.message")}
        open={false}
      ></MainNotification>
    </div>
  );
};

export default PrepOrdersView;
